import { useState, useEffect } from "react";
import { ChevronUp, FileText, Mail } from "lucide-react";

const WhosRankingTerms = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="whosranking-terms-page">
      <main className="pp-main" data-testid="whosranking-terms-content">
        <article className="pp-article">
          <header className="pp-header" data-testid="whosranking-terms-header">
            <div className="pp-header-icon">
              <FileText size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Terms of Use — Who's Ranking on G</h1>
            <p className="pp-last-updated">Last updated: October 4, 2026</p>
          </header>

          <section className="pp-section" data-testid="section-wrt-intro">
            <div className="pp-highlight-box">
              <p>
                These terms cover your use of the Who's Ranking on G iPhone app, made by
                Radhesh Gowd ("we", "us"). By using the app, you agree to them. The app is
                licensed to you under Apple's standard license agreement for App Store apps;
                these terms add to it.
              </p>
            </div>
          </section>

          <section className="pp-section" data-testid="section-wrt-what">
            <h2 className="pp-section-heading">What the app does</h2>
            <p>
              You enter a service and a city. The app shows which businesses rank on Google for
              that search, and which of them advertise on Google. It's free and needs no
              account.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-results">
            <h2 className="pp-section-heading">About the results</h2>
            <ul className="pp-list">
              <li>
                Results come from public Google search results and Google's public archive of
                ads, collected when you search. They may be saved for up to 7 days so repeat
                searches load quickly.
              </li>
              <li>
                Google's results change constantly and differ by location and by person, so
                what you see may not match what someone else sees, and it may be incomplete or
                out of date.
              </li>
              <li>
                "Running Google Ads" means we found recent ads for that business's website in
                Google's public ads archive. "Paying for top placement" means the business
                appeared in Google's paid listings when we checked. We can't see how much
                anyone spends.
              </li>
              <li>
                The results are information, not advice. Don't rely on them alone for business
                decisions.
              </li>
            </ul>
          </section>

          <section className="pp-section" data-testid="section-wrt-google">
            <h2 className="pp-section-heading">Not affiliated with Google</h2>
            <p>
              Who's Ranking on G is not affiliated with, endorsed by, or sponsored by Google.
              Google is a trademark of Google LLC.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-using">
            <h2 className="pp-section-heading">Using the app</h2>
            <p>
              Don't use the app to break the law, to harass anyone, or to copy, resell, or
              overload the service, for example by running automated searches.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-booking">
            <h2 className="pp-section-heading">
              Sending your details and booking a call
            </h2>
            <p>
              If you send us your details or book a call, we'll contact you about your request
              as described in our{" "}
              <a
                href="/whosrankingong"
                className="pp-link"
                data-testid="wrt-privacy-link"
              >
                Privacy Policy
              </a>
              . Bookings are handled by Cal.com under its own terms.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-liability">
            <h2 className="pp-section-heading">Our liability</h2>
            <p>
              The app is provided "as is". To the extent the law allows, we aren't liable for
              any loss that comes from using the app or relying on its results. Nothing in
              these terms limits rights you have by law that can't be excluded.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-changes">
            <h2 className="pp-section-heading">Changes</h2>
            <p>
              We may update these terms. When we do, the date at the top will change, and
              continuing to use the app means you accept the updated terms.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wrt-law">
            <h2 className="pp-section-heading">Governing law</h2>
            <p>These terms are governed by the laws of India.</p>
          </section>

          <section className="pp-section" data-testid="section-wrt-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <div className="pp-contact-card" data-testid="wrt-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="wrt-contact-email"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          <footer className="pp-footer" data-testid="whosranking-terms-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="wrt-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default WhosRankingTerms;
